import React, { useState } from "react";

export default function EMandateStep({ setCurrentStep, selectedOffer }) {
  // Track the selected authorization method
  const [authMethod, setAuthMethod] = useState("debit_card");

  // Initial state set to empty strings, ready for API/backend payload
  const [formData, setFormData] = useState({
    bankName: "",
    accountType: "",
    accountNumber: "",
    ifsc: "",
    accountName: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSetup = () => {
    console.log("Setting up eMandate via:", authMethod, formData);
    // Move to next step (e.g., eMandate external redirect or Success Page)
    setCurrentStep(15); 
  };

  const handleReport = () => {
    console.log("Discrepancy Reported");
    // Handle discrepancy logic (e.g., go back to bank details step)
    setCurrentStep(10);
  };

  const lenderName = selectedOffer?.lender_name || "CredFin";

  return (
    // Note: Increased bottom padding (pb-[160px]) to accommodate the two stacked buttons
    <div className="w-full max-w-[400px] mx-auto bg-white min-h-[100dvh] font-atkinson flex flex-col relative pb-[160px]">
      
      {/* Header & Progress Bar */}
      <div className="px-5 pt-6 pb-4 border-b border-transparent">
        <div className="flex justify-center mb-4">
          <div className="text-center leading-none">
            <h1 className="text-[#205A6D] font-bold text-[22px] tracking-tight">
              {lenderName}
            </h1>
            <p className="text-[#7ea4b1] text-[9px] font-semibold tracking-widest mt-1 uppercase">Loans made Easy</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <button onClick={() => setCurrentStep(13)} className="w-6 h-6 flex items-center justify-start text-gray-700">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          
          <div className="flex-1 mx-4 h-[4px] bg-[#e6f4ed] rounded-full overflow-hidden">
            {/* Progress updated to 69% */}
            <div className="h-full bg-[#12f280] w-[69%] rounded-full"></div>
          </div>
          
          <span className="text-[12px] text-gray-500 font-medium">69%</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-5 pt-2 flex-1">
        <h2 className="text-[22px] font-bold text-[#1b1b1b] mb-6">eMandate</h2>

        {/* Authorization Method */}
        <div className="mb-6">
          <label className="text-[13px] font-bold text-[#1b1b1b] block mb-3">
            Authorization by
          </label>
          <div className="flex gap-3">
            {/* Debit Card Radio */}
            <label 
              className={`flex-1 flex items-center gap-2.5 py-3.5 px-4 rounded-[12px] border cursor-pointer transition-colors ${
                authMethod === "debit_card" ? "border-black" : "border-[#e5e5e5]"
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-none ${
                authMethod === "debit_card" ? "border-black" : "border-[#ccc]"
              }`}>
                {authMethod === "debit_card" && <div className="w-2 h-2 bg-black rounded-full"></div>}
              </div>
              <input 
                type="radio" 
                name="authMethod" 
                className="hidden" 
                checked={authMethod === "debit_card"} 
                onChange={() => setAuthMethod("debit_card")} 
              />
              <span className="text-[14px] text-[#1b1b1b]">Debit Card</span>
            </label>

            {/* Net Banking Radio */}
            <label 
              className={`flex-1 flex items-center gap-2.5 py-3.5 px-4 rounded-[12px] border cursor-pointer transition-colors ${
                authMethod === "net_banking" ? "border-black" : "border-[#e5e5e5]"
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-none ${
                authMethod === "net_banking" ? "border-black" : "border-[#ccc]"
              }`}>
                {authMethod === "net_banking" && <div className="w-2 h-2 bg-black rounded-full"></div>}
              </div>
              <input 
                type="radio" 
                name="authMethod" 
                className="hidden" 
                checked={authMethod === "net_banking"} 
                onChange={() => setAuthMethod("net_banking")} 
              />
              <span className="text-[14px] text-[#1b1b1b]">Net Banking</span>
            </label>
          </div>
        </div>

        {/* Bank Details Grid */}
        <div className="flex flex-col gap-3">
          
          {/* Row 1: Bank Name & Account Type (Dropdowns) */}
          <div className="grid grid-cols-2 gap-3">
            {/* Bank Name Dropdown */}
            <div className="relative border border-[#e6e6e6] rounded-[12px] px-4 py-2 bg-white">
              <label className="text-[10px] text-[#8b8b8b] block mb-0.5">Bank Name</label>
              <select 
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                className="w-full outline-none text-[14px] text-[#1b1b1b] font-medium bg-transparent appearance-none pr-6 cursor-pointer"
              >
                <option value="" disabled hidden>Select Bank</option>
                <option value="Axis Bank">Axis Bank</option>
                <option value="HDFC Bank">HDFC Bank</option>
                <option value="ICICI Bank">ICICI Bank</option>
                <option value="SBI">SBI</option>
              </select>
              {/* Dropdown Chevron */}
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none text-[#a0a0a0]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </div>
            </div>

            {/* Account Type Dropdown */}
            <div className="relative border border-[#e6e6e6] rounded-[12px] px-4 py-2 bg-white">
              <label className="text-[10px] text-[#8b8b8b] block mb-0.5">Account Type</label>
              <select 
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
                className="w-full outline-none text-[14px] text-[#1b1b1b] font-medium bg-transparent appearance-none pr-6 cursor-pointer"
              >
                <option value="" disabled hidden>Select Type</option>
                <option value="Savings">Savings</option>
                <option value="Current">Current</option>
              </select>
              {/* Dropdown Chevron */}
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none text-[#a0a0a0]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </div>
            </div>
          </div>

          {/* Row 2: Account Number & IFSC */}
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-[#e6e6e6] rounded-[12px] px-4 py-2 bg-white focus-within:border-black transition-colors">
              <label className="text-[10px] text-[#8b8b8b] block mb-0.5">Account Number</label>
              <input 
                type="text" 
                name="accountNumber"
                placeholder="Enter Account Number"
                value={formData.accountNumber}
                onChange={handleChange}
                className="w-full outline-none text-[14px] text-[#1b1b1b] font-medium bg-transparent placeholder-[#cccccc]"
              />
            </div>

            <div className="border border-[#e6e6e6] rounded-[12px] px-4 py-2 bg-white focus-within:border-black transition-colors">
              <label className="text-[10px] text-[#8b8b8b] block mb-0.5">IFSC Code</label>
              <input 
                type="text" 
                name="ifsc"
                placeholder="Enter IFSC"
                value={formData.ifsc}
                onChange={handleChange}
                className="w-full outline-none text-[14px] text-[#1b1b1b] font-medium bg-transparent uppercase placeholder-[#cccccc]"
              />
            </div>
          </div>

          {/* Row 3: Account Holder Name */}
          <div className="border border-[#e6e6e6] rounded-[12px] px-4 py-2 bg-white focus-within:border-black transition-colors">
            <label className="text-[10px] text-[#8b8b8b] block mb-0.5">A/C Holder Name</label>
            <input 
              type="text" 
              name="accountName"
              placeholder="Enter Account Holder Name"
              value={formData.accountName}
              onChange={handleChange}
              className="w-full outline-none text-[14px] text-[#1b1b1b] font-medium bg-transparent placeholder-[#cccccc]"
            />
          </div>

        </div>
      </div>

      {/* Sticky Bottom Buttons (Dual Layout) */}
      <div className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full max-w-[400px] bg-white px-5 py-4 border-t border-transparent flex flex-col gap-3">
        {/* Secondary Button */}
        <button 
          onClick={handleReport} 
          className="w-full bg-[#F5F5F5] border border-[#E6E6E6] text-[#1b1b1b] font-medium text-[15px] rounded-[14px] py-[16px] active:scale-[0.98] transition-transform"
        >
          Report Discrepancy
        </button>
        
        {/* Primary Button */}
        <button 
          onClick={handleSetup} 
          className="w-full bg-black text-white font-bold text-[16px] rounded-[14px] py-[16px] active:scale-[0.98] transition-transform"
        >
          Set Up eMandate
        </button>
      </div>
    </div>
  );
}